import { useContracts, useProvider } from './useContract'
import { ABI_COCOS_TOKEN, ABI_LAUNCHPAD_POOL } from '../utils/abis'
import { useCallback, useEffect, useState } from 'react'
import { BigNumber } from 'ethers'
import { getMulContract } from '../utils'
// import httpApi from '../utils/httpApi'

import useHttp from 'hooks/useHttp'
import { BaseIGOInfo } from '../types'

export interface Token {
  address: string
  name: string
  symbol: string
  decimals: number
  totalSupply: BigNumber
}

export interface PoolInfo {
  address: string
  targetToken: Token
  costToken: Token
  oneShareAmount: BigNumber
  oneSharePrice: BigNumber
  totalShares: number
  raisedStartTime: number
  raisedEndTime: number
  totalRaisedUser: number
  hasRebase: boolean
  totalDrawUser: number
}

export function useIgoBaseInfo(id: number) {
  const $http = useHttp()
  const [igoInfo, setIgoInfo] = useState<BaseIGOInfo>(null)

  useEffect(() => {
    let unmounted = false

    const getInfo = async (id: number) => {
      const { data, status } = await $http.get(`/igo/project?id=${id}`)
      if (status === 200 && !unmounted) {
        setIgoInfo({
          ...data,
          social_: JSON.parse(data?.social),
          images_: JSON.parse(data?.images),
        })
      }
    }
    if (id) {
      getInfo(id)
    }
    return () => {
      unmounted = true
    }
  }, [id, $http])
  return igoInfo
}

export function useIgoPoolInfo(contractAddress: string | undefined) {
  const [poolInfo, setPoolInfo] = useState<PoolInfo | null>(null)
  const provider = useProvider()
  const { contract, multiCallContract } = useContracts(contractAddress, ABI_LAUNCHPAD_POOL)

  const load = useCallback(async () => {
    if (multiCallContract && provider) {
      try {
        const data = await provider.all([
          multiCallContract._targetToken(),
          multiCallContract._costToken(),
          multiCallContract._oneShareAmount(),
          multiCallContract._oneSharePrice(),
          multiCallContract._totalShares(),
          multiCallContract._raisedStartTime(),
          multiCallContract._raisedEndTime(),
          multiCallContract._totalRaisedUser(),
          multiCallContract._hasRebase(),
          multiCallContract._totalDrawUser(),
        ])
        const targetToken = getMulContract(data[0], ABI_COCOS_TOKEN)
        const costToken = getMulContract(data[1], ABI_COCOS_TOKEN)
        if (targetToken && costToken && contractAddress) {
          const tokenInfo = await provider.all([
            targetToken.name(),
            targetToken.symbol(),
            targetToken.decimals(),
            targetToken.totalSupply(),
            costToken.name(),
            costToken.symbol(),
            costToken.decimals(),
            costToken.totalSupply(),
          ])
          const info: PoolInfo = {
            // address: ADDRESS_LAUNCHPAD_POOL,
            address: contractAddress,
            targetToken: {
              address: data[0],
              name: tokenInfo[0],
              symbol: tokenInfo[1],
              decimals: +tokenInfo[2],
              totalSupply: tokenInfo[3],
            },
            costToken: {
              address: data[1],
              name: tokenInfo[4],
              symbol: tokenInfo[5],
              decimals: +tokenInfo[6],
              totalSupply: tokenInfo[7],
            },
            oneShareAmount: data[2],
            oneSharePrice: data[3],
            totalShares: +data[4],
            raisedStartTime: +data[5],
            raisedEndTime: +data[6],
            totalRaisedUser: +data[7],
            hasRebase: data[8],
            totalDrawUser: +data[9],
          }
          setPoolInfo(info)
        }
      } catch (e) {
        // console.log(e)
      }
    }
  }, [multiCallContract, contractAddress, provider])

  useEffect(() => {
    if (contractAddress) {
      load()
    }
    if (contract && contractAddress) {
      const listener = () => {
        setPoolInfo((prevState) => Object.assign({}, prevState, { hasRebase: true }))
      }
      const drawListener = (_: string, isIn: boolean) => {
        if (isIn) {
          contract._totalDrawUser().then((res: any) => {
            setPoolInfo((prevState) => Object.assign({}, prevState, { totalDrawUser: +res }))
          })
        }
      }
      contract.on('eveRebase', listener)
      contract.on('Draw', drawListener)
      return () => {
        contract.removeListener('eveRebase', listener)
        contract.removeListener('Draw', drawListener)
      }
    }
  }, [provider, contractAddress, load, contract])

  return poolInfo
}

export default function usePoolInfo(id: number) {
  const $http = useHttp()
  const [contractAddress, setContractAddress] = useState('')
  const provider = useProvider()
  const [poolInfo, setPoolInfo] = useState<PoolInfo | null>(null)
  const [baseIgoInfo, setBaseIgoInfo] = useState<BaseIGOInfo>(null)
  const { contract, multiCallContract } = useContracts(contractAddress, ABI_LAUNCHPAD_POOL)
  const load = useCallback(async () => {
    if (multiCallContract && provider) {
      try {
        const data = await provider.all([
          multiCallContract._targetToken(),
          multiCallContract._costToken(),
          multiCallContract._oneShareAmount(),
          multiCallContract._oneSharePrice(),
          multiCallContract._totalShares(),
          multiCallContract._raisedStartTime(),
          multiCallContract._raisedEndTime(),
          multiCallContract._totalRaisedUser(),
          multiCallContract._hasRebase(),
          multiCallContract._totalDrawUser(),
        ])
        const targetToken = getMulContract(data[0], ABI_COCOS_TOKEN)
        const costToken = getMulContract(data[1], ABI_COCOS_TOKEN)
        if (targetToken && costToken) {
          const tokenInfo = await provider.all([
            targetToken.name(),
            targetToken.symbol(),
            targetToken.decimals(),
            targetToken.totalSupply(),
            costToken.name(),
            costToken.symbol(),
            costToken.decimals(),
            costToken.totalSupply(),
          ])
          const info: PoolInfo = {
            // address: ADDRESS_LAUNCHPAD_POOL,
            address: contractAddress,
            targetToken: {
              address: data[0],
              name: tokenInfo[0],
              symbol: tokenInfo[1],
              decimals: +tokenInfo[2],
              totalSupply: tokenInfo[3],
            },
            costToken: {
              address: data[1],
              name: tokenInfo[4],
              symbol: tokenInfo[5],
              decimals: +tokenInfo[6],
              totalSupply: tokenInfo[7],
            },
            oneShareAmount: data[2],
            oneSharePrice: data[3],
            totalShares: +data[4],
            raisedStartTime: +data[5],
            raisedEndTime: +data[6],
            totalRaisedUser: +data[7],
            hasRebase: data[8],
            totalDrawUser: +data[9],
          }
          setPoolInfo(info)
        }
      } catch (e) {
        // console.log(e)
      }
    }
  }, [multiCallContract, contractAddress, provider])

  const getIgoInfo = useCallback(async () => {
    const { data, status } = await $http.get(`/igo/project?id=${id}`)
    if (status === 200) {
      setBaseIgoInfo({
        ...data,
        social_: JSON.parse(data?.social),
        images_: JSON.parse(data?.images),
      })
      setContractAddress(data.sale_contract)
    }
  }, [id, $http])

  useEffect(() => {
    if (id) {
      getIgoInfo()
    }

    if (contractAddress) {
      load()
    }
    if (contract && contractAddress) {
      const listener = () => {
        setPoolInfo((prevState) => Object.assign({}, prevState, { hasRebase: true }))
      }
      const drawListener = (_: string, isIn: boolean) => {
        if (isIn) {
          contract._totalDrawUser().then((res: any) => {
            setPoolInfo((prevState) => Object.assign({}, prevState, { totalDrawUser: +res }))
          })
        }
      }
      contract.on('eveRebase', listener)
      contract.on('Draw', drawListener)
      return () => {
        contract.removeListener('eveRebase', listener)
        contract.removeListener('Draw', drawListener)
      }
    }
  }, [id, getIgoInfo, provider, contractAddress, load, contract])
  return {
    poolInfo,
    baseIgoInfo,
  }
}
